import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  getCountFromServer,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import {
  FileText,
  Package,
  IndianRupee,
  AlertTriangle,
  Users,
  RefreshCw,
  Clock,
  Activity,
  ArrowUpDown,
  UserPlus,
  Store,
  Pencil,
  Trash2,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { PageHeader } from '@/components/PageHeader';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import type { InventoryItem } from '@/types/schemas';
import type { ActivityAction } from '@/lib/activityLog';

/* ─────────────────────── helpers ─────────────────────── */

function parseTimestamp(val: unknown): number {
  if (!val) return Date.now();
  if (typeof (val as { toMillis?: () => number }).toMillis === 'function') {
    return (val as { toMillis: () => number }).toMillis();
  }
  if (typeof val === 'number') return val;
  if (val instanceof Date) return val.getTime();
  return Date.now();
}

/* ─────────────────── component ──────────────────────── */

export function DashboardPage() {
  const qc = useQueryClient();

  /* ── 1. Total Quotations ────────────────────────────── */
  const quotationCount = useQuery({
    queryKey: ['dashboard', 'quotationCount'],
    queryFn: async () => {
      const snap = await getCountFromServer(collection(db, 'quotations'));
      return snap.data().count;
    },
  });

  /* ── 2. Total active inventory items (server-side count) ───────── */
  const totalItemsQuery = useQuery({
    queryKey: ['dashboard', 'totalItems'],
    queryFn: async () => {
      const snap = await getCountFromServer(
        query(collection(db, 'datahub_inventory'), where('isActive', '==', true)),
      );
      return snap.data().count;
    },
  });

  /* ── 3. Low stock count (server-side count) ─────────────────────── */
  const lowStockCountQuery = useQuery({
    queryKey: ['dashboard', 'lowStockCount'],
    queryFn: async () => {
      const snap = await getCountFromServer(
        query(
          collection(db, 'datahub_inventory'),
          where('isActive', '==', true),
          where('lowStock', '==', true),
        ),
      );
      return snap.data().count;
    },
  });

  /* ── 4. Total inventory value ─────────────────────────────────────
   * No stored per-item stock-value field to sum server-side yet, so this
   * still reads full documents — but only the active slice, not the whole
   * (including archived) catalog. See note in code review: precomputing
   * quantity*sellingPrice on write would let this become a sum() aggregation.
   */
  const totalValueQuery = useQuery({
    queryKey: ['dashboard', 'totalValue'],
    queryFn: async () => {
      const snap = await getDocs(
        query(collection(db, 'datahub_inventory'), where('isActive', '==', true)),
      );
      return snap.docs.reduce((acc, d) => {
        const it = d.data() as Omit<InventoryItem, 'id'>;
        return acc + (Number(it.quantity) || 0) * (Number(it.sellingPrice) || 0);
      }, 0);
    },
  });

  const inventoryMetrics = {
    data: {
      totalItems: totalItemsQuery.data ?? 0,
      lowStockCount: lowStockCountQuery.data ?? 0,
      totalValue: totalValueQuery.data ?? 0,
    },
    isLoading:
      totalItemsQuery.isLoading || lowStockCountQuery.isLoading || totalValueQuery.isLoading,
  };

  /* ── 5. Total Customers ─────────────────────────────── */
  const customerCount = useQuery({
    queryKey: ['dashboard', 'customerCount'],
    queryFn: async () => {
      const snap = await getCountFromServer(collection(db, 'customers'));
      return snap.data().count;
    },
  });

  /* ── Activity Logs ──────────────────────────────────── */
  const activityLogs = useQuery({
    queryKey: ['dashboard', 'activityLogs'],
    queryFn: async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'activity_logs'), orderBy('createdAt', 'desc'), limit(30)),
        );
        return snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            action: data.action as ActivityAction,
            title: data.title as string,
            description: data.description as string,
            actorName: (data.actorName as string) || null,
            entityType: data.entityType as string,
            timestamp: parseTimestamp(data.createdAt),
          };
        });
      } catch {
        return [];
      }
    },
  });

  const isMetricsLoading =
    quotationCount.isLoading || inventoryMetrics.isLoading || customerCount.isLoading;

  function refreshAll() {
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Company overview and recent activity"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAll}
            disabled={isMetricsLoading}
          >
            <RefreshCw className={cn('h-4 w-4', isMetricsLoading && 'animate-spin')} />
            <span className="hidden sm:inline ml-2">Refresh</span>
          </Button>
        }
      />

      {/* ── 5 Metric Cards ── */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          icon={<FileText className="h-4 w-4 text-gold-600" />}
          label="Total Quotations"
          value={String(quotationCount.data ?? 0)}
          hint="All created quotes"
          loading={quotationCount.isLoading}
          linkTo="/quotations"
        />
        <StatCard
          icon={<Package className="h-4 w-4 text-blue-600" />}
          label="Total Inventory Items"
          value={String(inventoryMetrics.data?.totalItems ?? 0)}
          hint="Active products in catalog"
          loading={inventoryMetrics.isLoading}
          linkTo="/datahub"
        />
        <StatCard
          icon={<IndianRupee className="h-4 w-4 text-emerald-600" />}
          label="Total Inventory Value"
          value={formatCurrency(inventoryMetrics.data?.totalValue ?? 0)}
          hint="Stock × Price"
          loading={inventoryMetrics.isLoading}
          linkTo="/datahub"
        />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4 text-rose-600" />}
          label="Low Stock Alert"
          value={String(inventoryMetrics.data?.lowStockCount ?? 0)}
          hint="Items below threshold"
          loading={inventoryMetrics.isLoading}
          linkTo="/datahub"
        />
        <StatCard
          icon={<Users className="h-4 w-4 text-purple-600" />}
          label="Total Customers"
          value={String(customerCount.data ?? 0)}
          hint="In CRM system"
          loading={customerCount.isLoading}
          linkTo="/crm"
        />
      </div>

      {/* ── Activity Logs ── */}
      <Card className="border shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-gold-600" />
            <CardTitle className="text-lg">Activity Logs</CardTitle>
          </div>
          <CardDescription>
            Recent activities — latest first
          </CardDescription>
        </CardHeader>

        <CardContent className="divide-y p-0">
          {activityLogs.isLoading && (
            <div className="p-6 space-y-4">
              <Skeleton rows={5} />
            </div>
          )}

          {!activityLogs.isLoading && (activityLogs.data ?? []).length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No activity logs yet. Actions like creating products, updating stock, and adding customers will appear here.
            </div>
          )}

          {!activityLogs.isLoading &&
            (activityLogs.data ?? []).map((log) => {
              const Icon = actionIcon(log.action);
              const color = actionColor(log.action);

              return (
                <div
                  key={log.id}
                  className="flex items-start gap-3 px-5 py-3 text-sm"
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg shadow-2xs mt-0.5',
                      color,
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{log.title}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 uppercase font-semibold tracking-wide">
                        {log.action.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {log.description}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                      {log.actorName && (
                        <span className="font-medium">{log.actorName}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(log.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────────── sub-components ─────────────────── */

function actionIcon(action: ActivityAction) {
  switch (action) {
    case 'product_created': return Package;
    case 'product_edited': return Pencil;
    case 'product_deleted': return Trash2;
    case 'stock_increased': return ArrowUpDown;
    case 'stock_decreased': return ArrowUpDown;
    case 'quotation_created': return FileText;
    case 'customer_created': return UserPlus;
    case 'store_updated': return Store;
    default: return Activity;
  }
}

function actionColor(action: ActivityAction): string {
  switch (action) {
    case 'product_created':
    case 'product_edited':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
    case 'product_deleted':
      return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
    case 'stock_increased':
    case 'stock_decreased':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300';
    case 'quotation_created':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
    case 'customer_created':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300';
    case 'store_updated':
      return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-muted animate-pulse shrink-0" />
          <div className="space-y-1.5 flex-1">
            <div className="h-3.5 w-36 rounded bg-muted animate-pulse" />
            <div className="h-2.5 w-48 rounded bg-muted animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  loading,
  linkTo,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
  linkTo?: string;
}) {
  const content = (
    <Card className={cn('h-full border shadow-2xs', linkTo && 'hover:shadow-md hover:border-gold-300 transition-all cursor-pointer')}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 px-4">
        <CardTitle className="text-xs font-medium text-muted-foreground leading-tight">
          {label}
        </CardTitle>
        <div className="shrink-0">{icon}</div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {loading ? (
          <div className="h-7 w-20 rounded bg-muted animate-pulse" />
        ) : (
          <div className="truncate font-serif text-xl font-bold tracking-tight">{value}</div>
        )}
        {hint && <p className="mt-1 text-[11px] text-muted-foreground truncate">{hint}</p>}
      </CardContent>
    </Card>
  );

  if (linkTo) return <Link to={linkTo} className="block h-full">{content}</Link>;
  return content;
}
